<!--
  ReopenModal.vue
  콜봇 월 정산 · `[재수정]` 확인창 — 화면 명세 2-4 "재수정", 요청 계약 4-2 `POST .../statements/:month/reopen`.

  확정된 정산을 검토 필요(재수정)로 되돌린다. 확인 문구는 요청 문서 그대로이고 사유 메모는 필수다.
  - 병원 상세 셸(SettlementDetail.vue)이 가진다. 헤더와 정산 탭은 'open-reopen' 만 보낸다.
  - 지금 확정 금액은 상세 `statement.total`(없으면 `charge.total`) — 공급가액이다.
  - 성공하면 알리고 닫은 뒤 reload() 로 상세를 다시 받는다(헤더·탭이 함께 "검토 필요(재수정)"로 바뀐다).
  - 409 STATEMENT_NOT_CONFIRMED(그새 확정이 아니게 됨)·404 STATEMENT_NOT_FOUND 는 알리고 닫은 뒤 다시 받는다.
    409 STATEMENT_CONFLICT(그 사이 자동 마감·다른 사람이 정산을 바꿈 — 회신 표 10)는 창을 연 채 알리고 상세를 다시
    받는다(쓴 메모를 두고, 바뀐 상태를 보고 다시 누른다). 그 밖의 실패는 창을 연 채 알린다(쓴 메모를 잃지 않게).
  - 수납(~/md/front/admin-콜봇-월정산-카드결제-프론트요청.md 결론 2·3-6, 계약 묶음 9.3 — 규칙은 settlementPayment.js):
    - 확인 문구 끝은 수납 갈래(`payment_scope`)로 고른다 — 결제가 걸린 달(in·null)은 받은 돈·멈춤·차액 안내,
      그 밖은 '청구'를 '정산'으로만 바꾼 문구.
    - 그 아래 읽기 전용 '수납 상태' 한 줄(PaymentStatusLine — 받은 금액이 있으면 경고 색), 장부가 결제 중·확인 중이면
      결과대로 정리 배너, 승인됨(처리 대기)이면 승인 무효 배너, 결제가 걸린 달이면 대표 동작 안내. 어느 것도 [재수정]을
      막지 않는다(서버가 정지 표식을 쓰지 못하면 503 PAYMENT_STOP_FAILED 로 막는다 — 아래).
    - 503 PAYMENT_STOP_FAILED(정지 표식을 쓰지 못함 — 요청 문서를 읽지 못함 포함)는 창을 연 채(쓴 메모 그대로) 알리고
      다시 받지 않는다(재수정은 되지 않았다. 잠시 뒤 그대로 다시 누른다).

  - 409 SETTLEMENT_NOT_STARTED(정산 시작일 전 달 — 남은 확정 정산서가 아니면 재수정도 막힌다)는 창을 닫고 셸의
    inject('settlementOnNotStarted') 로 알린 뒤 다시 받는다.

  - props: open · companyId · month · companyName · detail(상세 응답) · api · reload
  - emits: close
-->
<template>
  <a-modal :open="open" :footer="null" :mask-closable="false" width="520px" @cancel="handleCancel">
    <template #title>
      <div class="modal-title">재수정</div>
    </template>

    <div class="modal-content">
      <div class="reopen">
        <p class="reopen__target">{{ targetText }}</p>
        <p class="reopen__text">{{ confirmText }}</p>

        <!-- 수납(AC 결제 요청 결론 2·3-6) — 한 줄과 안내는 [재수정]을 막지 않는다 -->
        <PaymentStatusLine :detail="detail" highlight-received />
        <p v-if="paymentNotices.processing" class="reopen__text">{{ paymentNotices.processing }}</p>
        <p v-if="paymentNotices.scheduled" class="reopen__text">{{ paymentNotices.scheduled }}</p>
        <p v-if="paymentNotices.resellerActions" class="reopen__note">{{ paymentNotices.resellerActions }}</p>

        <label class="reopen__field">
          <span class="reopen__label">사유 메모 <em>*</em></span>
          <a-textarea
            v-model:value="memo"
            :rows="3"
            :maxlength="MEMO_MAX"
            show-count
            :disabled="submitting"
            placeholder="예: 대표번호 회선 판매가가 정해져 회선 금액을 넣고 다시 확정"
          />
        </label>
      </div>

      <div class="modal-footer">
        <a-button :disabled="submitting" @click="handleCancel">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="submitting" @click="handleSubmit">재수정</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import { message } from 'ant-design-vue';

import PaymentStatusLine from './PaymentStatusLine.vue';
import { isNumber, monthLabel } from '../service/settlementFormat';
import { STATEMENT_CONFLICT_TEXT, reopenConfirmText, statementBadge } from '../service/settlementRules';
import { PAYMENT_STOP_FAILED_TEXT, paymentScopeOf, reopenPaymentNotices } from '../service/settlementPayment';
import { isNotStartedError } from '../service/settlementStart';
import { getCallApiErrorCode, getCallApiErrorStatus, parseCallApiError } from '@/views/Callbot/service/callApiError';

const MEMO_MAX = 500;

const props = defineProps({
  open: { type: Boolean, required: true },
  companyId: { type: String, required: true },
  /** 'YYYY-MM'(KST) */
  month: { type: String, required: true },
  companyName: { type: String, default: '' },
  /** 정산 상세 응답(요청 계약 2) — 확정 금액과 확정 정보를 읽는다 */
  detail: { type: Object, default: null },
  /** settlementApi 또는 목(getSettlementApi()) */
  api: { type: Object, required: true },
  /** 상세를 다시 받는다(셸의 reload) */
  reload: { type: Function, required: true },
});

const emit = defineEmits(['close']);

/** 셸의 409 SETTLEMENT_NOT_STARTED 처리(안내 + 다시 받기). 셸 밖이면 null */
const onNotStarted = inject('settlementOnNotStarted', null);

const memo = ref('');
const submitting = ref(false);

// 열릴 때마다 비운다 — 닫았다 다시 열면 이전 메모가 남지 않게
watch(
  () => props.open,
  (open) => {
    if (open) memo.value = '';
  }
);

const confirmedTotal = computed(() => {
  const statementTotal = props.detail?.statement?.total;
  if (isNumber(statementTotal)) return statementTotal;
  const chargeTotal = props.detail?.charge?.total;
  return isNumber(chargeTotal) ? chargeTotal : null;
});

// 화면 명세 2-4 의 확인창 문구 그대로 — 끝은 수납 갈래로 고른다(AC 결제 요청 결론 2, 칸 없는 옛 call-api 는 inactive)
const confirmText = computed(() => reopenConfirmText(confirmedTotal.value, paymentScopeOf(props.detail)));

/** 수납 안내 — 결제 중·처리 대기 승인 배너와 대표 동작 안내(AC 결제 요청 결론 2·3-6). 해당하지 않으면 null */
const paymentNotices = computed(() => reopenPaymentNotices(props.detail));

const targetText = computed(() => {
  const name = props.companyName || props.detail?.company_name || props.companyId;
  return `${name} · ${monthLabel(props.month)} 정산 · ${statementBadge(props.detail?.statement ?? null).label}`;
});

function handleCancel() {
  if (submitting.value) return;
  emit('close');
}

async function handleSubmit() {
  if (submitting.value) return;
  const text = memo.value.trim();
  if (!text) {
    message.warning('재수정 사유 메모를 입력하세요.');
    return;
  }

  submitting.value = true;
  let refetch = false;
  try {
    await props.api.reopenStatement(props.companyId, props.month, { memo: text });
    message.success('검토 필요(재수정)로 되돌렸습니다. 다시 확정할 때까지 직전 확정 금액이 남습니다.');
    refetch = true;
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (isNotStartedError(err)) {
      // 정산 시작일 전 달이 됐다 — 닫고 셸이 알린 뒤 다시 받는다
      emit('close');
      if (onNotStarted) onNotStarted();
      else refetch = true;
    } else if (code === 'STATEMENT_NOT_CONFIRMED' || code === 'STATEMENT_NOT_FOUND') {
      // 그새 확정이 아니게 됐다(다른 사람의 재수정 등) — 지금 상태를 다시 받아 보인다
      message.warning('이 정산은 지금 확정 상태가 아니라 재수정할 수 없습니다. 최신 상태를 다시 불러옵니다.');
      refetch = true;
    } else if (code === 'STATEMENT_CONFLICT') {
      // 창은 연 채로 둔다 — 다 받을 때까지 버튼은 로딩. 다시 받은 상태가 아직 확정이면 그대로 다시 누르고, 아니면 셸이 창을 닫는다
      message.warning(STATEMENT_CONFLICT_TEXT);
      await props.reload();
    } else if (getCallApiErrorStatus(err) === 400) {
      message.warning(parseCallApiError(err, '재수정 사유 메모를 확인하세요.'));
    } else if (code === 'PAYMENT_STOP_FAILED') {
      // 503 — 결제를 먼저 멈추는 정지 표식을 쓰지 못해 재수정하지 않았다(계약 묶음 9.3). 창은 연 채로 두고
      // (쓴 메모 그대로) 다시 받지 않는다 — 잠시 뒤 그대로 다시 누른다
      message.error(PAYMENT_STOP_FAILED_TEXT);
    } else {
      message.error(parseCallApiError(err, '재수정하지 못했습니다.'));
    }
  } finally {
    submitting.value = false;
  }

  if (refetch) {
    emit('close');
    await props.reload();
  }
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

@include st-modal;
@include yellow-button;

.reopen {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  font-size: 13px;

  &__target {
    margin: 0;
    color: $st-muted;
    font-size: 12.5px;
  }

  &__text {
    margin: 0;
    padding: 10px 12px;
    border: 1px solid $st-warning-border;
    border-radius: 8px;
    background: $st-warning-bg;
    color: $st-text;
    line-height: 1.6;
  }

  // 결제가 걸린 달의 대표 동작 안내 — 막는 것이 아니라 알림이라 경고 상자 밖에 둔다
  &__note {
    margin: 0;
    color: $st-text-secondary;
    font-size: 12.5px;
    line-height: 1.6;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
  }

  &__label {
    color: $st-text;
    font-size: 13px;
    font-weight: 600;

    em {
      font-style: normal;
      color: $st-danger;
    }
  }
}
</style>
